import { useState, useCallback } from 'react';
import { message } from 'antd';
import { g204Api, FG140, FG141, FG142 } from '../../../services/g204Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useG204Mode';

export function useG204Record() {
  const [record, setRecord] = useState<FG140 | null>(null);
  const [formData, setFormData] = useState<Partial<FG140>>({});
  const [details, setDetails] = useState<Partial<FG141>[]>([]);
  const [subDetails, setSubDetails] = useState<Partial<FG142>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { clearFieldErrors, getFieldError, setFieldErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FG140;
    dets: Partial<FG141>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await g204Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      // Collect all sub_details from master level
      const subs = data.sub_details || [];
      setSubDetails(subs);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FG140, value: FG140[keyof FG140]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FG140): FG140[keyof FG140] => {
    return (formData as Record<string, FG140[keyof FG140]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FG141): FG141[keyof FG141] => {
    const sel = details[selectedRow] as Partial<FG141> | undefined;
    return sel ? (sel as Record<string, FG141[keyof FG141]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FG141, value: FG141[keyof FG141]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FG140) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FG140,
        details: details as FG141[],
        sub_details: subDetails as FG142[],
      };
      if (mode === 'add') {
        const result = await g204Api.create(req);
        message.success(`新增成功: ${result.fg140_01}`);
        onSuccess(result);
      } else {
        const result = await g204Api.update(formData.fg140_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        setSubDetails(result.sub_details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, subDetails, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await g204Api.delete(record.fg140_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row (BR-18: max 99) */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fg141_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('明細最多 99 項');
      return;
    }
    setDetails(prev => [...prev, {
      fg141_02: maxSeq + 1,
      fg141_01: formData.fg140_01 || '',
      fg141_12: (formData.fg140_12 || '') as string,
      fg141_13: 0,
      fg141_14: 0,
      fg141_17: 0,
    }]);
  }, [details, formData.fg140_01, formData.fg140_12]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    // Also remove associated sub_details for this row
    const deletedSeq = details[selectedRow]?.fg141_02;
    setSubDetails(prev => prev.filter(s => s.fg142_02 !== deletedSeq));
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fg141_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
      setSubDetails(record.sub_details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fg140_80: 'N',
      fg140_14: 0,
      fg140_15: 0,
      fg140_18: 0,
    });
    setDetails([]);
    setSubDetails([]);
    setSelectedRow(0);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FG140) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
    setSubDetails(result.sub_details || []);
  }, []);

  return {
    record, formData, details, subDetails, selectedRow, loading,
    setSelectedRow, setDetails, setSubDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
